import React, { useEffect, useState } from "react";
import { Loader2, Truck, Save, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import api, { formatINR, errMsg } from "@/lib/api";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { Input } from "@/components/ui/input";

const STATUSES = ["Order Confirmed", "Preparing", "Ready for Dispatch", "Shipped", "Out for Delivery", "Delivered"];

const AdminShipping = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState({});

  const load = () => {
    setLoading(true);
    api.get("/admin/orders").then((r) => {
      const paid = r.data.filter((o) => o.payment_status === "paid");
      setOrders(paid);
      const d = {};
      paid.forEach((o) => { d[o.order_id] = { courier_name: o.courier_name || "", awb_number: o.awb_number || "", tracking_url: o.tracking_url || "" }; });
      setDrafts(d);
      setLoading(false);
    });
  };
  useEffect(load, []);

  const setField = (oid, k, v) => setDrafts((d) => ({ ...d, [oid]: { ...d[oid], [k]: v } }));

  const saveCourier = async (o) => {
    try { await api.put(`/admin/orders/${o.order_id}/courier`, drafts[o.order_id]); toast.success(`Shipping saved for ${o.order_id}`); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const advance = async (o) => {
    const idx = STATUSES.indexOf(o.status);
    const next = STATUSES[Math.min(idx + 1, STATUSES.length - 1)];
    try { await api.put(`/admin/orders/${o.order_id}/status`, { status: next }); toast.success(`Marked ${next}`); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <AdminLayout title="Shipping">
      <p className="text-muted-foreground mb-6">Manage courier, AWB and tracking for confirmed orders. Ready for future courier-API integration.</p>
      {loading ? (
        <div className="flex justify-center py-24"><Loader2 size={36} className="animate-spin text-goldsoft" /></div>
      ) : orders.length === 0 ? (
        <div className="text-center py-24 text-muted-foreground">No confirmed orders to ship yet.</div>
      ) : (
        <div className="space-y-4">
          {orders.map((o) => (
            <div key={o.id} data-testid={`shipping-order-${o.order_id}`} className="bg-white rounded-2xl border border-[#E5DFD3] p-5 sm:p-6">
              <div className="flex flex-wrap justify-between items-center gap-2">
                <div>
                  <span className="font-display text-xl font-bold text-choco">{o.order_id}</span>
                  <span className="ml-3 text-xs px-2.5 py-1 rounded-full bg-blue-100 text-blue-800 font-medium">{o.status}</span>
                </div>
                <div className="text-right text-sm">
                  <p className="font-semibold text-choco">{o.full_name}</p>
                  <p className="text-muted-foreground">{o.city}, {o.state} - {o.pincode}</p>
                </div>
              </div>

              <div className="grid sm:grid-cols-3 gap-3 mt-4">
                <div><label className="text-xs font-medium text-choco">Courier Name</label><Input data-testid={`ship-courier-${o.order_id}`} value={drafts[o.order_id]?.courier_name} onChange={(e) => setField(o.order_id, "courier_name", e.target.value)} className="mt-1 h-11 bg-cream border-[#E5DFD3]" /></div>
                <div><label className="text-xs font-medium text-choco">AWB Number</label><Input data-testid={`ship-awb-${o.order_id}`} value={drafts[o.order_id]?.awb_number} onChange={(e) => setField(o.order_id, "awb_number", e.target.value)} className="mt-1 h-11 bg-cream border-[#E5DFD3]" /></div>
                <div><label className="text-xs font-medium text-choco">Tracking URL</label><Input data-testid={`ship-url-${o.order_id}`} value={drafts[o.order_id]?.tracking_url} onChange={(e) => setField(o.order_id, "tracking_url", e.target.value)} placeholder="https://..." className="mt-1 h-11 bg-cream border-[#E5DFD3]" /></div>
              </div>

              <div className="flex flex-wrap gap-2 mt-4 items-center">
                <button data-testid={`ship-save-${o.order_id}`} onClick={() => saveCourier(o)} className="inline-flex items-center gap-1.5 rounded-xl bg-choco text-cream text-sm font-medium px-4 py-2.5 hover:bg-chocodark min-h-[44px]"><Save size={16} /> Save Shipping</button>
                {o.status !== "Delivered" && <button data-testid={`ship-advance-${o.order_id}`} onClick={() => advance(o)} className="inline-flex items-center gap-1.5 rounded-xl bg-gold text-chocodark text-sm font-semibold px-4 py-2.5 hover:bg-goldsoft min-h-[44px]"><Truck size={16} /> Advance to next stage</button>}
                {drafts[o.order_id]?.tracking_url && <a href={drafts[o.order_id].tracking_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm text-choco hover:text-goldsoft px-2"><ExternalLink size={14} /> Open tracking</a>}
              </div>
            </div>
          ))}
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminShipping;
